// @ts-check
import {
  ArrowRightIcon,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '~/design-system';

/**
 * Renders the table with the sample rows
 * @param {Object} props
 * @param {[String[]]} props.sampleRows - the array of arrays with the data
 * @param {number} [props.headerRowIndex] - the sugested header row number based on previous imports
 * @param {number} [props.dataRowIndex] - the sugested row number where data starts based on previous imports
 * @param {number} props.columnsLimit - how many columns should be rendered
 * @param {object} [props.rest]
 */
export default function SampleRows({
  sampleRows,
  headerRowIndex,
  dataRowIndex,
  columnsLimit,
  ...rest
}) {
  return (
    <Box sx={{ justifyContent: 'center' }} {...rest}>
      <Table sx={{ margin: '0 auto' }}>
        <TableHead>
          <TableRow>
            <TableHeader sx={{ border: 'none' }} />
            <TableHeader />
            {sampleRows[0]?.map((header, i) => {
              if (columnsLimit && i + 1 > columnsLimit) return null;
              return (
                <TableHeader key={i} sx={{ textAlign: 'left' }}>
                  {toColumnLetter(i + 1)}
                </TableHeader>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {sampleRows?.map((row, i) => {
            let backgroundColor = '';
            let pointingRowText;
            if (headerRowIndex && i + 1 === headerRowIndex) {
              backgroundColor = 'brand.lightGreen';
              pointingRowText = 'Header row';
            }
            if (dataRowIndex && i + 1 >= dataRowIndex)
              backgroundColor = 'brand.lightestBlue';
            if (dataRowIndex && i + 1 === dataRowIndex)
              pointingRowText = 'Data starts here';
            return (
              <TableRow key={i} sx={{ border: 'none' }}>
                {pointingRowText ? (
                  <TableCell sx={{ textAlign: 'right', width: 150 }}>
                    <Text variant="smallest" sx={{ color: 'brand.smoke' }}>
                      {pointingRowText}
                      <ArrowRightIcon sx={{ marginLeft: 2 }} />
                    </Text>
                  </TableCell>
                ) : (
                  <TableCell sx={{ width: 150 }} />
                )}
                <BorderedCell>{i + 1}</BorderedCell>
                {row?.map((cell, j) => {
                  if (columnsLimit && j + 1 > columnsLimit) return null;
                  return (
                    <BorderedCell
                      key={j}
                      sx={{ backgroundColor, textAlign: 'left' }}
                    >
                      {cell}
                    </BorderedCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function BorderedCell({ children, ...rest }) {
  return (
    <TableCell sx={{ borderBottom: '1px solid #CCC' }} {...rest}>
      {children}
    </TableCell>
  );
}

/**
 * Converts a number to a excel column letter format
 * @param num
 * @return {*|string}
 */
function toColumnLetter(num) {
  const mod = num % 26;
  let pow = (num / 26) | 0;
  const out = mod ? String.fromCharCode(64 + mod) : (--pow, 'Z');
  return pow ? toColumnLetter(pow) + out : out;
}
